import { Injectable, inject } from "@angular/core";
import { firstValueFrom } from "rxjs";
import { FileUploadService } from "./file-upload.service";
import { MapInteractionService } from "./map-interaction.service";
import { PlanBlockStoreService } from "./plan-block-store.service";
import { FileReaderService } from "../../../viewer-components/services/readers/file-reader.service";
import {
  LAYER_TYPE_TO_STYLE_FN,
  resolveLayerContract,
  type EvidenceLayerRequest,
  type EvidenceLayerRequestGroup,
} from "../model/evidence-layer.types";
import type { ContentBlockData } from "../model/block.types";

/**
 * Max characters of the plan's `request_objective` kept in the group display name before the
 * ellipsis (evidence-layer-ui US4: `PlanData` has no display-name field — verified plan.types.ts).
 */
export const PLAN_GROUP_LABEL_MAX_LENGTH = 48;

/**
 * Deterministic truncation of the plan label backing the group display name: objectives up to
 * `PLAN_GROUP_LABEL_MAX_LENGTH` pass through unchanged; longer ones keep their first
 * `PLAN_GROUP_LABEL_MAX_LENGTH` characters (trailing whitespace trimmed) plus an ellipsis.
 */
export function truncatePlanLabel(objective: string): string {
  if (objective.length <= PLAN_GROUP_LABEL_MAX_LENGTH) return objective;
  return `${objective.slice(0, PLAN_GROUP_LABEL_MAX_LENGTH).trimEnd()}…`;
}

/**
 * The SINGLE chat-side door for adding `data.layer` contract blocks to the map
 * (evidence-layer-ui US3/US4). It builds a typed, map-state-free `EvidenceLayerRequest`
 * (validation only) and drives the proven manual-add flow — fetch the block's file ref,
 * prepare the GeoJSON, emit one `add_evidence_layer` map event — with no automatic drawing:
 * callers (the add actions/chips) invoke it explicitly.
 *
 * Blocks WITHOUT the contract keep today's exact behavior: `buildRequest` returns
 * `undefined` and `addToMapFromBlock` is a logged no-op, so the legacy `addGeoJsonLayer`
 * path stays untouched.
 *
 * `PlanBlockStoreService` is optional (same convention as the content dispatcher and the plan
 * progress card): it is provided per chat-container, so a root-scoped instantiation of this
 * service sees no store and the group descriptor simply carries no display name — the map side
 * derives a fallback from the group key.
 */
@Injectable({ providedIn: "root" })
export class EvidenceLayerService {
  readonly #fileUpload = inject(FileUploadService);

  readonly #fileReader = inject(FileReaderService);

  readonly #map = inject(MapInteractionService);

  readonly #planStore = inject(PlanBlockStoreService, { optional: true });

  /**
   * Builds the evidence-layer add request for a block's `data.layer` contract, reading NO map
   * state. `undefined` when the block carries no contract (US1: renders exactly as today).
   */
  buildRequest(block: ContentBlockData): EvidenceLayerRequest | undefined {
    const contract = resolveLayerContract(block);
    if (!contract) return undefined;

    const request: EvidenceLayerRequest = {
      styleFunctionName: LAYER_TYPE_TO_STYLE_FN[contract.type],
    };

    const label = contract.label ?? block.label;
    if (label) request.name = label;

    if (contract.plan_ref !== null) {
      const group: EvidenceLayerRequestGroup = { key: `plan:${contract.plan_ref}` };
      // The contract's plan_ref duplicates source.plan_id (backend design), while the plan
      // store indexes blocks by block plan_ref — join through plan_id first, fall back to
      // the block ref. Without this the group falls back to the raw key as display name.
      const plan = this.#planStore?.byPlanId(contract.plan_ref) ?? this.#planStore?.byPlanRef(contract.plan_ref) ?? null;
      // Title policy: display_name ?? request_objective (|| also treats an empty string as
      // absent — display_name is null for pre-change plans and during creation, so the
      // fallback is mandatory). The FULL long objective is kept as the tooltip description.
      const title = plan?.display_name || plan?.request_objective;
      if (typeof title === "string" && title.length > 0) {
        group.name = truncatePlanLabel(title);
      }
      const longObjective = plan?.request_objective;
      if (typeof longObjective === "string" && longObjective.length > 0) {
        group.description = longObjective;
      }
      request.group = group;
    }

    return request;
  }

  /**
   * Adds a contract block's geometry to the map through the proven manual-add mechanism
   * (fetch + prepare + emit flow, parametrized by the request — US3): fetches
   * `ref.url` (never a reconstructed URL), prepares the GeoJSON via
   * `FileReaderService.prepareGeoJson`, and emits the prepared collection together with the
   * request on the new `add_evidence_layer` map event. Failures are logged, never thrown into
   * the caller's stream.
   */
  async addToMapFromBlock(block: ContentBlockData): Promise<void> {
    const request = this.buildRequest(block);
    if (!request) {
      console.warn("EvidenceLayerService: block has no data.layer contract — nothing to add");
      return;
    }

    const ref = block.ref;
    if (!ref) {
      console.error("EvidenceLayerService: evidence block has no file ref — nothing to add", request);
      return;
    }

    try {
      const result = await firstValueFrom(
        this.#fileUpload.fetchFileContent(ref.url, ref.mime_type, {
          filename: ref.filename,
          size_bytes: ref.size_bytes,
        }),
      );
      if (result.type !== "geojson") {
        console.error(`EvidenceLayerService: evidence arrived as ${result.type}, expected geojson`, request);
        return;
      }
      const prepared = this.#fileReader.prepareGeoJson(result.geojson as any, ref.filename);
      if (!prepared.data) {
        console.error("EvidenceLayerService: GeoJSON preparation failed", prepared.errors as unknown, request);
        return;
      }
      this.#map.addEvidenceLayer(prepared.data, request);
    } catch (err) {
      console.error("EvidenceLayerService: evidence fetch failed", ref.url, err);
    }
  }
}
